import { v4 } from 'uuid';
import { makeObservable, observable } from 'mobx';
import * as _ from 'lodash';
import { BaseListener, BaseObserver } from '../utils/BaseObserver';
import * as React from 'react';
import { JourneyNavigationHandler, Routes } from '../routes';

export interface LayerRenderEvent<T extends Routes = Routes.ADMIN_HOME> {
  index: number;
  layer: Layer;
  handler: JourneyNavigationHandler<T>;
}

export interface LayerOptions {
  render: (event: LayerRenderEvent) => JSX.Element;
  clickThrough?: boolean;
  userCanExit?: boolean;
  alwaysOnTop?: boolean;
  withBackdrop?: boolean;
}

export interface LayerListener extends BaseListener {
  dispose: () => any;
  update: () => any;
}

export class Layer<T extends LayerOptions = LayerOptions> extends BaseObserver<LayerListener> {
  id: string;
  options: T;

  @observable
  clickThrough: boolean;

  constructor(options: T) {
    super();
    this.id = v4();
    this.options = options;
    this.clickThrough = options.clickThrough ?? false;
    makeObservable(this);
  }

  update() {
    this.iterateListeners((cb) => cb.update?.());
  }

  userCanExit() {
    return this.options.userCanExit ?? true;
  }

  dispose() {
    this.iterateListeners((cb) => cb.dispose?.());
  }
}

export interface LayerManagerListener extends BaseListener {
  update: () => any;
}

export class LayerManager extends BaseObserver<LayerManagerListener> {
  @observable
  protected visibleLayers: Layer[];

  constructor() {
    super();
    this.visibleLayers = [];
    makeObservable(this);
  }

  getLayers(): Layer[] {
    return _.sortBy(this.visibleLayers, (l) => (l.options.alwaysOnTop ? 1 : 0));
  }

  protected removeLayer(layer: Layer) {
    const index = this.visibleLayers.findIndex((f) => f.id === layer.id);
    if (index === -1) {
      return;
    }
    this.visibleLayers.splice(index, 1);
  }

  protected addLayer(layer: Layer) {
    // layer already exists
    if (this.visibleLayers.find((f) => f.id === layer.id)) {
      return;
    }
    this.visibleLayers.push(layer);
  }

  registerLayer(layer: Layer) {
    this.addLayer(layer);
    const l = layer.registerListener({
      dispose: () => {
        this.removeLayer(layer);
        l();
      },
      update: () => {
        this.iterateListeners((cb) => cb.update?.());
      }
    });
  }

  showLayer(layerOptions: LayerOptions): Layer {
    const layer = new Layer<LayerOptions>(layerOptions);
    this.registerLayer(layer);
    return layer;
  }
}
